import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useAuth } from './AuthProvider';
import { OperadorInfo, PermissionModule, PERMISSION_LABELS } from '@/types/permissions';
import { getUserPermissions, updateModulePermissions } from '@/integrations/supabase/permissions';
import { toast } from 'sonner';
import { Loader2, Users, FileText, ShoppingCart, DollarSign, Package, BarChart3, Ruler, Calendar, Grid3x3, Wrench, Truck, Receipt, Settings } from 'lucide-react';

interface DialogEditarPermissoesProps {
  operador: OperadorInfo;
  onClose: () => void;
}

const MODULES: { id: PermissionModule; label: string; description: string; icon: React.ReactNode; actions: string[] }[] = [
  {
    id: 'clientes',
    label: 'Clientes',
    description: 'Gerenciar clientes e contatos',
    icon: <Users className="w-5 h-5" />,
    actions: ['view', 'create', 'edit', 'delete', 'export']
  },
  {
    id: 'orcamentos',
    label: 'Orçamentos',
    description: 'Criar e gerenciar orçamentos',
    icon: <FileText className="w-5 h-5" />,
    actions: ['view', 'create', 'edit', 'cancel', 'delete', 'alter_values', 'apply_discount']
  },
  {
    id: 'pedidos',
    label: 'Pedidos',
    description: 'Gerenciar pedidos e produção',
    icon: <ShoppingCart className="w-5 h-5" />,
    actions: ['view', 'create', 'edit', 'cancel', 'delete']
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    description: 'Controle financeiro e pagamentos',
    icon: <DollarSign className="w-5 h-5" />,
    actions: ['view', 'create', 'edit', 'delete', 'export']
  },
  {
    id: 'estoque',
    label: 'Estoque',
    description: 'Gerenciar inventário',
    icon: <Package className="w-5 h-5" />,
    actions: ['view', 'entrada', 'saida', 'ajuste_manual']
  },
  {
    id: 'relatorios',
    label: 'Relatórios',
    description: 'Acessar relatórios',
    icon: <BarChart3 className="w-5 h-5" />,
    actions: ['view', 'export']
  },
  {
    id: 'medidas',
    label: 'Medidas',
    description: 'Gerenciar tabelas de medidas',
    icon: <Ruler className="w-5 h-5" />,
    actions: ['view', 'create', 'edit', 'delete']
  },
  {
    id: 'agenda',
    label: 'Agenda',
    description: 'Gerenciar agenda e eventos',
    icon: <Calendar className="w-5 h-5" />,
    actions: ['view', 'create', 'edit', 'delete']
  },
  {
    id: 'catalogo',
    label: 'Catálogo',
    description: 'Gerenciar catálogo de produtos',
    icon: <Grid3x3 className="w-5 h-5" />,
    actions: ['view', 'create', 'edit', 'delete']
  },
  {
    id: 'servicos',
    label: 'Serviços',
    description: 'Gerenciar serviços oferecidos',
    icon: <Wrench className="w-5 h-5" />,
    actions: ['view', 'create', 'edit', 'delete']
  },
  {
    id: 'fornecedores',
    label: 'Fornecedores',
    description: 'Gerenciar fornecedores',
    icon: <Truck className="w-5 h-5" />,
    actions: ['view', 'create', 'edit', 'delete']
  },
  {
    id: 'notas_fiscais',
    label: 'Notas Fiscais',
    description: 'Emitir e gerenciar notas',
    icon: <Receipt className="w-5 h-5" />,
    actions: ['view', 'create', 'edit', 'delete']
  },
  {
    id: 'configuracoes',
    label: 'Configurações',
    description: 'Configurações do sistema',
    icon: <Settings className="w-5 h-5" />,
    actions: ['view', 'manage_users', 'manage_payments']
  }
];

export function DialogEditarPermissoes({ operador, onClose }: DialogEditarPermissoesProps) {
  const { empresa } = useAuth();
  const empresaId = empresa?.id;
  const queryClient = useQueryClient();
  const [permissions, setPermissions] = useState<Record<string, Record<string, boolean>>>({});

  const { data: currentPermissions = {}, isLoading } = useQuery({
    queryKey: ['user-permissions', operador.id, empresaId],
    queryFn: () =>
      empresaId ? getUserPermissions(operador.id, empresaId) : Promise.resolve({})
  });

  useEffect(() => {
    setPermissions(currentPermissions);
  }, [currentPermissions]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!empresaId) throw new Error('Empresa não encontrada');

      for (const module of MODULES) {
        const actions = permissions[module.id] || {};
        await updateModulePermissions(operador.id, empresaId, module.id, actions);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operadores', empresaId] });
      toast.success('Permissões atualizadas com sucesso');
      onClose();
    },
    onError: (error) => {
      console.error(error);
      toast.error('Erro ao atualizar permissões');
    }
  });

  const handleActionChange = (module: PermissionModule, action: string, checked: boolean) => {
    setPermissions((prev) => ({
      ...prev,
      [module]: {
        ...(prev[module] || {}),
        [action]: checked
      }
    }));
  };

  if (isLoading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader className="mb-2">
          <DialogTitle className="text-2xl">Permissões de {operador.full_name}</DialogTitle>
          <DialogDescription className="text-base">
            Customize quais módulos e funcionalidades este operador pode acessar
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {MODULES.map((module) => (
            <Card key={module.id} className="border border-gray-200 overflow-hidden hover:border-purple-300 transition-colors">
              <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="text-purple-600 mt-0.5">
                      {module.icon}
                    </div>
                    <div>
                      <CardTitle className="text-base text-gray-900">{module.label}</CardTitle>
                      <p className="text-xs text-gray-600 mt-0.5">{module.description}</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  {module.actions.map((action) => {
                    const isChecked = permissions[module.id]?.[action] ?? false;
                    return (
                      <div
                        key={action}
                        className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <label className="flex items-center gap-3 cursor-pointer flex-1">
                          <span className="text-sm text-gray-700 font-medium">
                            {PERMISSION_LABELS[action] || action}
                          </span>
                        </label>
                        <Switch
                          checked={isChecked}
                          onCheckedChange={(checked) =>
                            handleActionChange(module.id, action, checked)
                          }
                          className="ml-3"
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex gap-3 justify-end pt-6 border-t">
          <Button variant="outline" onClick={onClose} disabled={saveMutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Salvar Permissões
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
